import { useQueryClient } from '@tanstack/react-query'
import { Logger, type DecoratedWidgetData, type IOType, type WidgetId, type WidgetListResponse } from '@terros/common'
import { widgetKey, widgetListKey } from './keys'

const logger = new Logger('useWidgetCache')

type WidgetCache = {
  clearItem: (widgetId: WidgetId) => void
  clearList: VoidFunction
  clearWidgets: VoidFunction
  setList: (ioType: IOType, widgets: DecoratedWidgetData[]) => void
}

export const useWidgetCache = (): WidgetCache => {
  const queryClient = useQueryClient()

  return {
    clearItem: (widgetId: WidgetId) => {
      logger.verbose('removeQueries widgetKey', widgetId)
      queryClient.removeQueries({ queryKey: widgetKey() })
      queryClient.setQueriesData<WidgetListResponse>({ queryKey: widgetListKey() }, (prev) => {
        if (!prev) return
        return {
          inputWidgets: prev.inputWidgets.filter((widget) => widget.widgetId !== widgetId),
          outputWidgets: prev.outputWidgets.filter((widget) => widget.widgetId !== widgetId),
        }
      })
    },
    clearList: () => {
      logger.verbose('invalidateQueries widgetListKey')
      queryClient.invalidateQueries({ queryKey: widgetListKey() })
      queryClient.removeQueries({ queryKey: widgetKey() })
    },
    clearWidgets: () => {
      queryClient.removeQueries({ queryKey: widgetKey() })
    },
    setList: (ioType: IOType, widgets: DecoratedWidgetData[]): void => {
      queryClient.setQueriesData<WidgetListResponse>({ queryKey: widgetListKey() }, (prev) => {
        if (!prev) return
        const key = ioType === 'Input' ? 'inputWidgets' : 'outputWidgets'
        return {
          ...prev,
          [key]: widgets,
        }
      })
    },
  }
}
